import React, { Component, ReactNode, RefObject, ComponentType } from 'react';
import { connect } from 'react-redux';
import ReactDOM from 'react-dom';
import {
	SceneItemData,
	Orientation,
	Platform,
	DeviceType,
	PopOver,
	CoverItem,
	PopOverSceneItemData,
	Settings,
	AppView,
	Lightbox,
	PredefinedConfiguration,
	SofaConfigurationItem,
	SofaConfiguration,
	RangeSettings,
	WriteDirection,
	SceneItemCoverConnection,
	SceneHelperData,
	ArmrestsByCover,
	ShoppingItem,
	IModuleSwiperItem,
} from 'sofa-reborn-react';
import { RootState, AppDispatch } from '../store';
import {
	setCurrentLightbox,
	setHasFirstInteractionHappened,
	setCoverChanged,
} from '../store/application/actions';
import {
	pointerOverDropArea,
	resetCameraRotation,
	setNoRotationIcon,
	removeSceneItemOutsideScene,
	setKeepModule,
	setRotateCamera,
	moveBackrestAsArmrest,
	backrestAsArmrest,
	setSpecificSelectedItems,
} from '../store/scene/actions';
import {
	setDragItemId,
	setSwiperEventType,
	setModuleItemsChanged,
	setSelectedItemId,
} from '../store/swiper/actions';
import { showPopOver, hidePopOver, buildTipHasBeenShown, hideBuildTip } from '../store/popOver';
import Scene from '../components/Scene';
import {
	View3dElement,
	ProductData,
	SceneObject,
	TransformVector,
} from '@ikea-aoa/component-view-3d';
import { Groups, ModelData } from '@ikea-aoa/component-view-3d/lib/main/connection-manager';
import {
	setUpdatePredefinedConfiguration,
	setPersistedConfiguration,
	setShoppingItems,
} from '../store/sofaConfiguration';
import { setLoadingModels, setLoadingCovers } from '../store/application/actions';
import { setFaultySections, setUnfinishedModule } from '../store/missing';
import {
	setSceneData,
	selectSceneItem,
	selectSceneArticle,
	setMeasurements,
	animateCameraRotation,
	dragSceneItemId,
	overSceneItemId,
	rotateSceneItem,
	moveBackrest,
	setDisableMoveBackrest,
	setDisableMoveBackrestAsArmrest,
	removeAllSceneItems,
	setSceneItemCover,
	setColorForAll,
	setSceneHelper,
	addCushions,
} from '../store/scene';
import {
	setShowFunctionSleep,
	setShowFunctionStorage,
	setShowFunctions,
	setShowMeasurements,
	setShowSelectAll,
} from '../store/actionButtons';
import { screenshotSuccess, screenshotPending, setTwoSeparateSofas } from '../store/checkout';
import { clearAdditionalCoverItems, setNotPreloadedCovers } from '../store/cover/actions';
import { setSelectedArmrest } from '../store/armrest/actions';
import { ProductUtils } from '../util/ProductUtils';
import { setCoverItem } from '../store/application/thunks';
import { CameraController } from './SceneContainer/CameraController';
import { AssetsUrls } from '../util/SceneUtils/AssetsUrls';
import { localeService } from '../services';
import { DebounceManager } from '@inter-ikea-kompis/utilities';

import { SceneLogic } from './SceneContainer/SceneLogic';
import { ScenePointer } from './SceneContainer/ScenePointer';
import { UiController } from './SceneContainer/UiController';
import { SceneDataUtils } from './SceneContainer/SceneDataUtils';
import { Product } from '@ikea-aoa/component-view-3d';

const DEFAULT_LIGHT_INTENSITY = 1.6;
const ENVIRONMENTAL_LIGHTING = {
	name: 'studio23',
	intensity: 3.5,
};

// Props actually set on "this" object
type OwnProps = {
	showConnectionPoints?: boolean;
	ref?: RefObject<SceneContainer>;
};

// Props fetched from redux store
type StateProps = {
	rangeSettings: RangeSettings;
	assetFolder: string | null;
	orientation: Orientation;
	platform: Platform;
	deviceType: DeviceType;
	dragItemId: string;
	selectedItemId: string;
	products: ProductData;
	sofaModels: ModelData;
	sofaGroups: Groups;
	moduleItems: IModuleSwiperItem[];
	selectedConfiguration: PredefinedConfiguration;
	predefinedConfigurations: PredefinedConfiguration[];
	useMetricMeasures: boolean | null;
	coverItems: CoverItem[];
	settings: Settings;
	appView: AppView;
	currentLightbox: Lightbox;
	loadingModels: boolean;
	loadingCovers: boolean;
	resetCameraRotation: boolean;
	cover: CoverItem;
	showDebug?: boolean;
	useIndividualAssets: boolean;
	screenshotPending: boolean | null;
	showFunctionSleep?: boolean;
	showFunctionStorage?: boolean;
	showFunctions?: boolean;
	showMeasurements?: boolean;
	showSelectAll?: boolean;
	selectedSceneItem?: PopOverSceneItemData;
	updatePredefinedConfiguration?: boolean;
	removeSceneItem?: SceneItemData;
	rotateSceneItem?: SceneItemData;
	moveBackrest?: SceneItemData;
	moveBackrestAsArmrest?: SceneItemData;
	backrestAsArmrest: SceneItemData | undefined;
	removeAllSceneItems?: boolean;
	addCushions?: boolean;
	setSceneItemCover?: SceneItemCoverConnection;
	colorForAll?: string;
	animateCameraRotation?: number;
	writeDirection?: WriteDirection;
	environmentalLighting?: string;
	lightIntensity?: number;
	sceneHelper?: SceneHelperData;
	unfinishedSection?: PopOver;
	hasVisitScene: boolean;
	moduleItemsChanged: boolean;
	noValidConnectionPopoverShowing: boolean | undefined;
	notPreloadedCovers: string[] | null;
	selectedArmrest: Product | null;
	armrests: ArmrestsByCover | null;
	coverOnly: boolean;
	keepModule: boolean;
	isInUpptackaPlatform: boolean;
	swiperEventType: 'click' | 'drag' | null;
	specificItems: Map<string, SceneObject>;
	moduleSwiperItems: Array<IModuleSwiperItem>;
};

// Dispatch props
type DispatchProps = {
	setDragItemId: (value: string | null) => void;
	setSwiperEventType: (value: { type: 'click' | 'drag' | null; productId: string | null }) => void;
	setSelectedItemId: (value: string | null) => void;
	setNoRotationIcon: (value: boolean) => void;
	setHasFirstInteractionHappened: (value: boolean) => void;
	pointerOverDropArea: (value: boolean) => void;
	removeSceneItemOutsideScene: (sceneObject: SceneObject) => void;
	saveSceneData: (
		sceneItems: SceneItemData[],
		sofaConfiguration: SofaConfiguration,
		articlesPlaceable: { [key: string]: boolean },
		backrestsMissingCushion: boolean,
		rangeSettings: RangeSettings,
		sofaModels: ModelData,
		products: ProductData,
		isOverDropArea: boolean
	) => void;
	setShoppingItems: (shoppingItems: ShoppingItem[]) => void;
	setLoadModels: (value: boolean) => void;
	setLoadCovers: (value: boolean) => void;
	setUnfinishedModule: (value: PopOverSceneItemData) => void;
	setResetCameraRotation: (value: boolean) => void;
	dragSceneItemId: (value: string | null) => void;
	overSceneItemId: (value: string | null) => void;
	showPopOver: (value: PopOver) => void;
	hidePopOver: (value: PopOver) => void;
	setScreenshotPending: () => void;
	saveScreenshot: (screenshot: string) => void;
	selectSceneItem: (item: PopOverSceneItemData | null) => void;
	setRotateSceneItem: (item: SceneItemData | null) => void;
	setMoveBackrest: (item: SceneItemData | null) => void;
	setMoveBackrestAsArmrest: (item: SceneItemData | null) => void;
	setBackrestAsArmrest: (item: SceneItemData | null) => void;
	setRemoveAllSceneItems: (value: boolean) => void;
	setAddCushions: (value: boolean) => void;
	changeSceneItemCover: (sceneItemCoverConnection: SceneItemCoverConnection | null) => void;
	setColorForAll: (value: string | null) => void;
	setShowSelectAll: (value: boolean) => void;
	setShowFunctionSleep: (value: boolean, track: boolean) => void;
	setShowFunctionStorage: (value: boolean, track: boolean) => void;
	setShowFunctions: (value: boolean) => void;
	setShowMeasurements: (value: boolean, track: boolean) => void;
	setMeasurements: (value: string) => void;
	setAnimateCameraRotation: (value: number | null) => void;
	setCoverItem: (value: CoverItem, coverUpdated: boolean) => void;
	setCurrentLightbox: (value: Lightbox) => void;
	setUpdatePredefinedConfiguration: (value: boolean) => void;
	setPersistedConfiguration: (value: string) => void;
	setFaultySections: (
		unfinishedSections: PopOverSceneItemData[],
		missingCoverSections: PopOverSceneItemData[],
		standaloneSofabed: PopOverSceneItemData[]
	) => void;
	setSceneHelper: (value: SceneHelperData | null) => void;
	setModuleItemsChanged: (value: boolean) => void;
	setCoverChanged: (value: Lightbox | null) => void;
	setNotPreloadedCovers: (value: string[] | null) => void;
	setSelectedArmrest: (value: Product | null, track: boolean) => void;
	setKeepModule: (value: boolean) => void;
	rotateCamera: () => void;
	setDisableMoveBackrest: (value: boolean) => void;
	setDisableMoveArmrestAsBackrest: (value: boolean) => void;
	clearAdditionalCoverItems: () => void;
	setTwoSeparateSofas: (value: boolean) => void;
	setSpecificItems: (items: Map<string, SceneObject>) => void;
	setBuildTipHasBeenShown: (value: boolean) => void;
	setHideBuildTip: (value: boolean) => void;
};

type Props = OwnProps & StateProps & DispatchProps;

// eslint-disable-next-line @typescript-eslint/ban-types
type State = {};

export class SceneContainer extends Component<Props, State> {
	private scene: View3dElement;
	private logic: SceneLogic;
	private cameraController: CameraController;
	private uiController: UiController;
	private scenePointer: ScenePointer;
	private sceneDataUtils: SceneDataUtils;
	private sceneHeight = 0;

	private resizeObserver: ResizeObserver;
	private debounce = new DebounceManager();

	private urls: Map<string, string[]> = new Map<string, string[]>();

	constructor(props: Props) {
		super(props);
		this.resizeObserver = new ResizeObserver(this.onResize);
	}

	public componentDidMount(): void {
		this.updateManagers();
		this.observeScene();
	}

	public componentDidUpdate(prevProps: Props): void {
		this.updateManagers();

		if (this.props.resetCameraRotation) {
			if (this.cameraController) {
				this.resetDefaultCameraRotation();
			}
			this.props.setResetCameraRotation(false);
		}

		let itemRemoved = false;
		if (this.props.removeSceneItem && !prevProps.removeSceneItem) {
			const item = this.props.removeSceneItem;
			const sceneObject = this.logic.objectManager.sceneItems.get(item.id);
			if (sceneObject) {
				const modelData = this.logic.connectionManager.getModelData(sceneObject.articleId);

				// Destroy automatically added modules if the removed module is not a child
				if (modelData && !modelData.logic?.addAsChild) {
					this.logic.objectManager.automaticallyAddManager.remove(
						this.props.rangeSettings.rangeName !== 'JÄTTEBO'
					);
				}

				// Remove backrest if its an armrest
				if (this.props.specificItems) {
					const sceneItemsArr = Array.from(this.logic.sceneItems.values());
					this.logic.sceneItems.forEach((item) => {
						if (sceneItemsArr.includes(item) && item.articleId.includes('backrest')) {
							this.logic.objectManager.destroyObject(item);
						}
					});
				}

				// Destroy object
				this.logic.objectManager.automaticallyAddManager.remove(
					this.props.rangeSettings.rangeName !== 'JÄTTEBO'
				);
				this.logic.objectManager.destroyObject(sceneObject);
				this.logic.objectManager.automaticallyAddManager.create();
				this.logic.objectManager.legsManager.updateLegs(
					!this.props.rangeSettings.coversWithNoLegAssembly?.includes(this.props.cover.coverName)
				);

				// ui
				this.props.selectSceneItem(null);
				this.uiController.unselect();
				this.uiController.updateTotalMeasurements();

				// camera
				this.cameraController.focusCamera();
				this.saveScreenshot();

				itemRemoved = true;
			} else {
				throw 'SceneContainer: componentDidUpdate(): sceneObject does not exist';
			}
		}

		let itemRotated = false;
		if (this.props.rotateSceneItem) {
			const item = this.props.rotateSceneItem;
			const sceneObject = this.logic.sceneItems.get(item.id);

			// Rotate object
			if (sceneObject && this.logic.rotate(sceneObject)) {
				this.props.setRotateSceneItem(null);

				// camera
				this.cameraController.focusCamera();

				// ui
				this.uiController.setPopOverSceneItemData(sceneObject);
				this.uiController.updateTotalMeasurements();

				itemRotated = true;
			} else {
				throw 'SceneContainer: componentDidUpdate(): sceneObject does not exist';
			}
		}

		if (this.props.moveBackrest) {
			const item = this.props.moveBackrest;
			const sceneObject = this.logic.sceneItems.get(item.id);
			// Move backrest
			if (sceneObject) {
				this.props.setMoveBackrest(null);
				if (this.logic.objectManager.automaticallyAddManager.move(sceneObject)) {
					this.props.selectSceneItem(null);
					this.uiController.unselect();
					this.uiController.updateTotalMeasurements();
					this.saveScreenshot(() => this.uiController.selectItem(sceneObject));
				}
			} else {
				throw 'SceneContainer: componentDidUpdate(): sceneObject does not exist';
			}
		}

		if (this.props.moveBackrestAsArmrest) {
			const item = this.props.moveBackrestAsArmrest;
			const sceneItemsArr = Array.from(this.logic.sceneItems.values());
			const childSceneObject = sceneItemsArr.find((sceneItem) => sceneItem.parentId === item.id);
			const parentSceneObject = this.logic.sceneItems.get(item.id);
			if (this.props.backrestAsArmrest && parentSceneObject) {
				this.props.setBackrestAsArmrest(null);
				this.logic.objectManager.automaticallyAddManager.changeSeatConnection(parentSceneObject);
				this.logic.objectManager.automaticallyAddManager.remove(true);
				this.logic.objectManager.automaticallyAddManager.create();
				this.saveScreenshot(() => this.uiController.selectItem(parentSceneObject));
				this.props.setSpecificItems(new Map<string, SceneObject>());
			} else if (
				childSceneObject &&
				parentSceneObject &&
				this.logic.objectManager.automaticallyAddManager.moveBackrest(
					childSceneObject,
					parentSceneObject
				)
			) {
				const parentSceneItemData = this.uiController.getPopOverSceneItemData(parentSceneObject);
				this.props.setBackrestAsArmrest(parentSceneItemData);
				const specificItems = new Map<string, SceneObject>();
				this.logic.sceneItems.forEach((item) => {
					if (item.id === childSceneObject.id || item.id === parentSceneObject.id) {
						specificItems.set(item.id, item);
					}
				});
				this.props.setSpecificItems(specificItems);
				this.saveScreenshot(() => this.uiController.selectSpecificItems(specificItems));
			} else {
				throw 'SceneContainer: componentDidUpdate(): sceneObject does not exist';
			}
			this.props.setMoveBackrestAsArmrest(null);
			this.uiController.updateTotalMeasurements();
		}

		let allItemsRemoved = false;
		if (this.props.removeAllSceneItems) {
			this.clearScene();
			this.uiController.updateTotalMeasurements();
			this.props.setRemoveAllSceneItems(false);
			this.props.setShowSelectAll(false);
			allItemsRemoved = true;
		}

		let addedCushions = false;
		if (this.props.addCushions) {
			this.logic.objectManager.cushionManager.createAutoCushionObjects();
			this.props.setAddCushions(false);
			addedCushions = true;
		}

		if (this.props.showSelectAll && !prevProps.animateCameraRotation) {
			this.uiController.selectAll();
		}

		const previousColor = prevProps.setSceneItemCover
			? prevProps.setSceneItemCover.coverItem
			: null;
		const previousItem = prevProps.setSceneItemCover ? prevProps.setSceneItemCover.sceneItem : null;
		let itemCoverChanged = false;
		if (
			this.props.setSceneItemCover &&
			!(
				previousColor === this.props.setSceneItemCover.coverItem &&
				previousItem === this.props.setSceneItemCover.sceneItem
			)
		) {
			const cover = this.props.setSceneItemCover.coverItem;
			const item = this.props.setSceneItemCover.sceneItem;
			this.logic.objectManager.setCover(item.id, cover.coverName);
			// update selectedSceneItem after changing its cover
			const object = this.logic.sceneItems.get(item.id);
			if (
				object &&
				this.props.selectedSceneItem &&
				this.props.selectedSceneItem.articleId !== object.articleId
			) {
				const sceneItemData = this.uiController.getPopOverSceneItemData(object);
				if (sceneItemData) {
					this.props.selectSceneItem(sceneItemData);
				} else {
					throw 'SceneContainer: componentDidUpdate(): sceneItemData does not exist';
				}
			}
			itemCoverChanged = true;
		}

		let colorAll = false;
		if (this.props.colorForAll) {
			this.props.setColorForAll(null);
			colorAll = true;
		}

		let modelsAreLoaded = false;
		if (!this.props.loadingModels && this.props.loadingModels !== prevProps.loadingModels) {
			modelsAreLoaded = true;
		}

		const {
			cover,
			orientation,
			screenshotPending,
			selectedSceneItem,
			updatePredefinedConfiguration,
			animateCameraRotation,
			moduleItemsChanged,
			selectedArmrest,
			coverOnly,
		} = this.props;
		const coverIsChanged = cover.articleId !== prevProps.cover.articleId;
		const orientationIsChanged = orientation !== prevProps.orientation;
		const predefinedConfigurationIsChanged =
			this.props.selectedConfiguration !== prevProps.selectedConfiguration;
		const showFunctionSleepChanged = this.props.showFunctionSleep !== prevProps.showFunctionSleep;
		const showFunctionStorageChanged =
			this.props.showFunctionStorage !== prevProps.showFunctionStorage;
		const showFunctionsChanged = this.props.showFunctions !== prevProps.showFunctions;
		const showMeasurementsChanged = this.props.showMeasurements !== prevProps.showMeasurements;
		const showSelectAllChanged = this.props.showSelectAll !== prevProps.showSelectAll;
		const selectedSceneItemChanged =
			selectedSceneItem !== prevProps.selectedSceneItem &&
			(selectedSceneItem && selectedSceneItem.id) !==
				(prevProps.selectedSceneItem && prevProps.selectedSceneItem.id);
		const armrestChanged = selectedArmrest && selectedArmrest !== prevProps.selectedArmrest;
		const coverOnlyChanged = coverOnly !== prevProps.coverOnly;
		const backrestHasBeenMoved =
			this.props.moveBackrestAsArmrest !== prevProps.moveBackrestAsArmrest;

		if (armrestChanged) {
			const configuration = this.logic?.getSofaConfiguration();
			if (
				!configuration?.configuration?.children?.some((child) => {
					return child.children?.some((c) => c.articleId === selectedArmrest?.productId);
				})
			) {
				this.logic.objectManager.automaticallyAddManager.remove(
					this.props.rangeSettings.rangeName !== 'JÄTTEBO'
				);
				this.logic.objectManager.automaticallyAddManager.create();
				this.uiController.updateTotalMeasurements();
			}
		}

		if (
			coverIsChanged ||
			showFunctionSleepChanged ||
			showFunctionStorageChanged ||
			showFunctionsChanged ||
			selectedSceneItemChanged ||
			modelsAreLoaded ||
			colorAll
		) {
			// Update the scene items if the modules should update after cover change
			if (
				this.props.rangeSettings.shouldColorAllModules ||
				showFunctionSleepChanged ||
				showFunctionStorageChanged ||
				showFunctionsChanged ||
				colorAll
			) {
				this.logic.objectManager.setCoverForAllItems(this.props.colorForAll);
				if (!showFunctionSleepChanged || !showFunctionStorageChanged || !showFunctionsChanged) {
					this.uiController.unselect();
				}
			}

			this.logic.objectManager.legsManager.updateLegs(
				!this.props.rangeSettings.coversWithNoLegAssembly?.includes(this.props.cover.coverName)
			);
		}

		if (this.uiController && !selectedSceneItem && !this.props.showSelectAll) {
			this.uiController.unselect();
		}

		if (orientationIsChanged) {
			// ui
			this.uiController.hideAllPopOvers();
			this.uiController.unselect();
			this.props.selectSceneItem(null);
		}

		if (showMeasurementsChanged) {
			// ui
			this.uiController.unselect();
			this.uiController.updateTotalMeasurements();

			if (this.cameraController) {
				this.cameraController.focusCamera();
			}
		}

		if (showSelectAllChanged) {
			if (this.cameraController) {
				this.cameraController.focusCamera();
			}
		}

		if (
			updatePredefinedConfiguration ||
			(predefinedConfigurationIsChanged && this.props.selectedConfiguration)
		) {
			this.props.setUpdatePredefinedConfiguration(false);
			this.clearScene();

			if (!this.props.selectedConfiguration.configuration?.children) {
				this.uiController.updateTotalMeasurements();
			}

			// Update colorId to the color of the predefined combination or vpc code combination
			if (
				this.props.selectedConfiguration &&
				this.props.selectedConfiguration.configuration &&
				this.props.selectedConfiguration.configuration.children &&
				this.props.selectedConfiguration.configuration.children.length > 0 &&
				this.props.selectedConfiguration.configuration.children[0].articleId
			) {
				const coverName = ProductUtils.getColorIdFromProduct(
					this.props.products,
					this.props.selectedConfiguration.configuration.children[0].articleId
				); // color id??
				this.logic.objectManager.setColorId(coverName);
			}

			this.buildPredefinedConfiguration(this.props.selectedConfiguration);
		}

		if (updatePredefinedConfiguration || coverIsChanged || itemCoverChanged || colorAll) {
			/* Preload selected color urls if not already preloaded */
			if (this.props.deviceType === 'mobile' && this.scene.isIOS() && this.urls.size > 0) {
				if (
					this.props.cover &&
					this.props.notPreloadedCovers &&
					this.props.notPreloadedCovers.includes(this.props.cover.coverName)
				) {
					const urlsToFetch: string[] = [];
					const selectedUrls = this.urls.get(this.props.cover.coverName);
					if (selectedUrls) {
						const scene = this.scene;
						const props = this.props;
						selectedUrls.forEach(function (url) {
							const isPreloaded = scene.isPreloaded(url);

							if (!isPreloaded) {
								urlsToFetch.push(url);
							}
						});

						if (urlsToFetch.length > 0) {
							props.setLoadModels(true);
							scene.preload(urlsToFetch);
							this.props.setCoverChanged(
								this.props.currentLightbox === 'product-intro-gallery'
									? 'none'
									: this.props.currentLightbox
							);
							/* Remove cover from not loaded covers array */
							if (
								this.props.notPreloadedCovers &&
								this.props.notPreloadedCovers.includes(this.props.cover.coverName)
							) {
								const newPreloadedCovers = this.props.notPreloadedCovers.filter(
									(cover) => cover !== this.props.cover.coverName
								);
								this.props.setNotPreloadedCovers(newPreloadedCovers);
							}
						}
					}
				}
			}
		}

		if (
			updatePredefinedConfiguration ||
			coverIsChanged ||
			itemRemoved ||
			itemRotated ||
			itemCoverChanged ||
			colorAll ||
			allItemsRemoved ||
			addedCushions ||
			moduleItemsChanged ||
			armrestChanged ||
			coverOnlyChanged ||
			backrestHasBeenMoved
		) {
			if (moduleItemsChanged) {
				this.props.setModuleItemsChanged(false);
			}
			this.updateSceneData();
		}

		if (animateCameraRotation) {
			this.cameraController.animateCameraRotation(animateCameraRotation);
			this.props.setAnimateCameraRotation(null);
			// ui
			this.uiController.unselect();
		}

		if (
			(screenshotPending && screenshotPending !== prevProps.screenshotPending) ||
			updatePredefinedConfiguration ||
			coverIsChanged
		) {
			this.saveScreenshot();
		}
	}

	public componentWillUnmount(): void {
		this.unObserveScene();
		this.debounce.clearAll();

		if (this.cameraController) {
			this.cameraController.clearCameraRotationLock();
		}
	}

	private observeScene = (): void => {
		const sceneElement = ReactDOM.findDOMNode(this);
		if (sceneElement) {
			this.resizeObserver.observe(sceneElement as Element);
		}
	};

	private unObserveScene = (): void => {
		const sceneElement = ReactDOM.findDOMNode(this);
		if (sceneElement) {
			this.resizeObserver.unobserve(sceneElement as Element);
		}
	};

	private onResize = (element: ResizeObserverEntry[]): void => {
		if (element) {
			if (this.scene) {
				this.debounce.set('resize', () => {
					this.scene.resize();
				});
			}
		}
	};

	private onCameraUpdated = (): void => {
		this.debounce.set(
			'onCameraUpdated',
			() => {
				// Camera view has changed forward the new screen positions to whom it may concern.
				const unfinishedSections = !this.logic.isModularSofa()
					? this.logic
							.getUnfinishedSections()
							.map((i) => this.uiController.getPopOverSceneItemData(i))
					: [];
				const missingCoverSections = this.logic
					.getMissingCoverSections()
					.map((i) => this.uiController.getPopOverSceneItemData(i));
				const standaloneSofabed = this.logic
					.getStandaloneSofabed()
					.map((i) => this.uiController.getPopOverSceneItemData(i));
				this.props.setFaultySections(
					unfinishedSections as PopOverSceneItemData[],
					missingCoverSections as PopOverSceneItemData[],
					standaloneSofabed as PopOverSceneItemData[]
				);

				const noDrag = this.props.dragItemId === null;
				if (this.sceneDataUtils && !this.props.keepModule && noDrag) {
					this.sceneDataUtils.updateNoValidPopover();
				}
			},
			200
		);
	};

	private updateManagers = (): void => {
		if (this.logic) {
			this.logic.updateProps(this.props);
			this.cameraController.updateProps(this.props);
			this.uiController.updateProps(this.props);
			this.scenePointer.updateProps(this.props);
			this.sceneDataUtils.updateProps(this.props);
		}
	};

	private saveScreenshot = (callback?): void => {
		this.cameraController.createScreenshot((screenshot) => {
			callback && callback();
			this.props.saveScreenshot(screenshot);
			if (this.logic.isModularSofa()) {
				const twoSeparate = this.logic.hasTwoSeparateSofas(this.logic.sceneItems);
				this.props.setTwoSeparateSofas(twoSeparate);
			}
		});
	};

	private onSceneReady = (e: CustomEvent): void => {
		this.scene = e.target as View3dElement;
		this.sceneHeight = document.getElementById('applicationWrapper')?.clientHeight || 0;

		if (
			this.props.useIndividualAssets ||
			(this.props.deviceType === 'mobile' && this.scene.isIOS())
		) {
			this.urls = AssetsUrls.getModelUrls(
				this.props.assetFolder,
				this.props.rangeSettings.assets3dSrc,
				this.props.products,
				this.props.sofaModels,
				this.props.rangeSettings.modelSwitchMap || null
			);
			/* To many covers we need to lazy load (load 8 covers) */
			if (this.props.coverItems.length > 10 && !this.props.useIndividualAssets) {
				let urlsToLoad: string[] = [];
				const coversToLoad = this.getCoversToLoad(this.props.predefinedConfigurations);
				const urls = this.urls;
				coversToLoad.forEach(function (cover) {
					if (urls.has(cover)) {
						const coverUrls = urls.get(cover);
						if (coverUrls) {
							urlsToLoad = urlsToLoad.concat(coverUrls);
						}
					}
				});
				/* Add legs url, if they exist */
				if (
					this.props.products['leg'] &&
					this.props.sofaModels[this.props.products['leg'].modelId]
				) {
					urlsToLoad = urlsToLoad.concat(this.getLegUrl());
				}
				const notPreloadedCovers = this.getNotPreloadedCovers(this.props.coverItems, coversToLoad);
				this.props.setNotPreloadedCovers(notPreloadedCovers);
				this.scene.preload(urlsToLoad);
			} else {
				/* Preload all covers */
				let allUrls: string[] = [];
				this.urls.forEach(function (url) {
					allUrls = allUrls.concat(url);
				});
				/* Add legs url, if they exist */
				if (
					this.props.products['leg'] &&
					this.props.sofaModels[this.props.products['leg'].modelId]
				) {
					allUrls = allUrls.concat(this.getLegUrl());
				}
				this.scene.preload(allUrls);
			}
		} else {
			// Default to package
			let path = 'package';
			if (
				this.props.rangeSettings.separatePackages &&
				!localeService.getApplicationSetting('hasIkeaPlus')
			) {
				// Load normal IKEA package
				path = 'packageIkea';
			}
			const assetsPackage = AssetsUrls.getUrlPackage(
				this.props.assetFolder,
				this.props.rangeSettings.assets3dSrc,
				this.props.products,
				this.props.sofaModels,
				path,
				this.props.rangeSettings.modelSwitchMap || null
			);
			this.scene.preloadPackage(assetsPackage);
		}

		this.createLighting();

		this.logic = new SceneLogic(this.scene, this.props);
		this.uiController = new UiController(this.scene, this.logic, this.props);
		this.cameraController = new CameraController(
			this.scene,
			this.logic.moduleConnectionManager,
			this.props
		);
		this.sceneDataUtils = new SceneDataUtils(this.logic, this.uiController, this.props);
		this.scenePointer = new ScenePointer(
			this.scene,
			this.logic,
			this.uiController,
			this.cameraController,
			this.sceneDataUtils,
			this.props.rangeSettings,
			this.props.deviceType,
			this.props.products,
			this.props
		);

		this.props.setLoadModels(true);
		this.uiController.AddMeasurements();
	};

	private onModelsPreloaded = (): void => {
		this.props.setLoadModels(false);
		if (this.props.loadingCovers && this.props.deviceType === 'mobile' && this.scene.isIOS()) {
			this.props.setLoadCovers(false);
		}

		if (this.props.screenshotPending == null) {
			this.saveScreenshot();
		}

		if (this.props.isInUpptackaPlatform) {
			window.parent.postMessage(
				{
					method: 'upptacka_application_loaded',
				},
				'*'
			);
		}
	};

	private onPointerOverGround = (): void => this.scenePointer.onPointerOverGround();

	private onPointerOverNode = (e: CustomEvent): void => this.scenePointer.onPointerOverNode(e);

	public onPointerDown = (e: CustomEvent): void => this.scenePointer.onPointerDown(e);

	public onPointerMove = (e: CustomEvent): void => this.scenePointer.onPointerMove(e);

	public onPointerUp = (e?: CustomEvent): void => this.scenePointer.onPointerUp(e);

	public onKeyDown = (): void => this.scenePointer.onKeyDown();

	private onSceneResized = (): void => {
		if (this.cameraController) {
			this.cameraController.focusCamera(true);
		}
	};

	private createLighting(): void {
		const useEnvironmental =
			this.props.rangeSettings.useEnvironmentalLighting || this.props.environmentalLighting;
		if (useEnvironmental) {
			const mapsUrl = this.props.rangeSettings.environmentMapsSrc;
			let name: string;
			let intensity: string | number;
			if (this.props.environmentalLighting) {
				[name, intensity] = this.props.environmentalLighting.split('-');
			} else {
				({ name, intensity } = ENVIRONMENTAL_LIGHTING);
			}
			const url = AssetsUrls.getEnvironmentalTextureUrl(mapsUrl, name);
			const int = typeof intensity == 'string' ? parseFloat(intensity) : intensity;
			this.scene.createEnvironment(url, int);
		}
		const intensity =
			this.props.lightIntensity || this.props.lightIntensity == 0
				? this.props.lightIntensity
				: !useEnvironmental
				? DEFAULT_LIGHT_INTENSITY
				: 0;
		if (intensity !== 0) {
			this.scene.createLight(new TransformVector(0, 1, 0), intensity);
		}
	}

	private updateSceneData(): void {
		if (this.sceneDataUtils) {
			this.sceneDataUtils.updateSceneData();
		}
	}

	private resetDefaultCameraRotation = (
		predefinedConfiguration?: PredefinedConfiguration
	): void => {
		if (
			(predefinedConfiguration && !predefinedConfiguration.configuration.children) ||
			!this.cameraController
		) {
			return;
		} else if (this.logic.isModularSofa() || this.props.rangeSettings.rangeName === 'LIDHULT') {
			// Temp fix
			this.cameraController.resetRotation();
			return;
		}

		const angles: number[] = [];
		let mf = 1;
		let m = 0;
		let mostOccuringRotation = 0;
		let articleId: string | undefined;
		let cornerRotation: number | null = null;
		const cornerRotations: number[] = [];

		// If predefined as a parameter it will add all childrens angles to the angles array
		if (
			predefinedConfiguration &&
			predefinedConfiguration.configuration &&
			predefinedConfiguration.configuration.children
		) {
			predefinedConfiguration.configuration.children.forEach((child: SofaConfigurationItem) => {
				articleId = child.articleId as string;
				const family = this.logic.connectionManager.getFamily(articleId);
				if (child.rot && (child.rot.ry || child.rot.ry == 0)) {
					if (family == 'CORNER') {
						cornerRotations.push(Math.round((child.rot.ry + Number.EPSILON) * 10000) / 10000);
					}
					if (family == 'CORNER' || family == 'SEAT' || family == 'CHAISE' || family == 'SOFABED') {
						angles.push(Math.round((child.rot.ry + Number.EPSILON) * 10000) / 10000);
					}
				}
			});
			// If no parameter it will add all scene items angles to the angles array
		} else if (this.logic.sceneItems) {
			this.logic.sceneItems.forEach((item) => {
				articleId = item.articleId;
				const family = this.logic.connectionManager.getFamily(articleId);
				const rotation = this.logic.connectionManager.getWorldRotation(item);
				if (rotation && (rotation.y || rotation.y == 0)) {
					if (family == 'CORNER') {
						cornerRotations.push(
							Math.round((rotation.y / (Math.PI / 180) + Number.EPSILON) * 10000) / 10000
						);
					}
					if (family == 'CORNER' || family == 'SEAT' || family == 'CHAISE' || family == 'SOFABED') {
						angles.push(Math.round((rotation.y + Number.EPSILON) * 10000) / 10000);
					}
				}
			});
		}

		// Calculate which angle is the most occuring in the configuration
		angles.forEach((angle) => {
			angles.forEach((angle2) => {
				if (angle == angle2) {
					m++;
				}
				if (mf < m) {
					mf = m;
					mostOccuringRotation = angle;
				}
			});
			m = 0;
		});

		// Calculate wanted rotation if there are multiple corners
		if (cornerRotations.length > 1) {
			cornerRotation = cornerRotations.reduce((a, b) => a + b, 0) / 2 - 30;
		} else if (cornerRotations.length) {
			cornerRotation = cornerRotations[0];
		}

		// Some magic below
		const rotation: number = predefinedConfiguration
			? cornerRotation || cornerRotation == 0
				? (135 - cornerRotation) * (Math.PI / 180)
				: (315 - Math.abs(mostOccuringRotation)) * (Math.PI / 180)
			: cornerRotation || cornerRotation == 0
			? (135 - cornerRotation) * (Math.PI / 180)
			: 225 * (Math.PI / 180);

		// If the most occuring rotation is 0 it is considered to be correct already
		if (mostOccuringRotation !== 0) {
			this.cameraController.rotateCamera(rotation);
		} else {
			this.cameraController.resetRotation();
		}
	};

	private buildPredefinedConfiguration = (
		predefinedConfiguration: PredefinedConfiguration
	): void => {
		if (this.cameraController) {
			this.cameraController.reset();
		}
		if (
			!predefinedConfiguration ||
			!predefinedConfiguration.configuration ||
			!predefinedConfiguration.configuration.children
		) {
			return;
		}

		let valid = true;
		// Create scene object for each section in the configuration
		predefinedConfiguration.configuration.children.forEach((child: SofaConfigurationItem) => {
			valid = this.createPredefinedSceneObjectsRecursive(child);
		});

		if (!valid) {
			// Not able to create some of the predefined objects.
			this.clearScene();
			this.uiController.updateTotalMeasurements();
			return;
		} else {
			this.logic.moduleConnectionManager.connectAll();
		}
		this.logic.objectManager.legsManager.updateLegs(
			!this.props.rangeSettings.coversWithNoLegAssembly?.includes(this.props.cover.coverName)
		);

		if (this.cameraController) {
			this.cameraController.focusCamera();
		}

		if (
			predefinedConfiguration.configuration.children.length > 0 &&
			predefinedConfiguration.configuration.children[0].articleId
		) {
			const coverName = ProductUtils.getColorIdFromProduct(
				this.props.products,
				predefinedConfiguration.configuration.children[0].articleId
			);

			if (coverName) {
				const coverItem = this.props.coverItems.find((coverItem) => {
					return coverItem.coverName == coverName;
				});

				if (coverItem && coverItem.articleId !== this.props.cover.articleId) {
					this.props.setCoverItem(coverItem, false);
				}
			} else {
				throw 'SceneContainer: buildPredefinedConfiguration(): coverName does not exist';
			}
		}

		const isWide = predefinedConfiguration.configuration.children.some(
			(child: SofaConfigurationItem) => {
				return child.children?.some((child) => {
					return this.logic.connectionManager.isWideArmrest(child.articleId);
				});
			}
		);
		if (this.props.armrests && this.props.armrests[this.props.cover.coverName]) {
			const articleId = predefinedConfiguration.configuration.children[0].articleId;
			let configurationCover;

			if (articleId) {
				configurationCover = ProductUtils.getColorIdFromProduct(this.props.products, articleId);
			}
			const coverName =
				predefinedConfiguration.defaultColor &&
				this.props.armrests[predefinedConfiguration.defaultColor]
					? predefinedConfiguration.defaultColor
					: configurationCover || this.props.cover.coverName;
			if (isWide) {
				this.props.setSelectedArmrest(this.props.armrests[coverName].wideArmrest, false);
			} else {
				this.props.setSelectedArmrest(this.props.armrests[coverName].regularArmrest, false);
			}
		}

		this.resetDefaultCameraRotation(predefinedConfiguration);
		this.uiController.updateTotalMeasurements();
	};

	private createPredefinedSceneObjectsRecursive = (
		sofaConfigurationItem: SofaConfigurationItem
	): boolean => {
		let valid = true;
		sofaConfigurationItem.articleId =
			sofaConfigurationItem.articleId && sofaConfigurationItem.articleId.replace('G', '');
		const object = this.createPredefinedSceneObject(sofaConfigurationItem);
		if (!object) {
			valid = false;
		}
		if (valid && object) {
			const children = sofaConfigurationItem.children as SofaConfigurationItem[];
			children.forEach((child) => {
				valid = this.createPredefinedSceneObjectsRecursive(child);
			});
		}
		return valid;
	};

	private createPredefinedSceneObject = (
		predefinedItem: SofaConfigurationItem
	): SceneObject | false => {
		const isPrevious = ProductUtils.isPreviousId(
			this.props.products,
			predefinedItem.articleId as string
		);
		const articleId = isPrevious
			? ProductUtils.getNewIdFromPrevious(this.props.products, predefinedItem.articleId as string)
			: predefinedItem.articleId;
		if (predefinedItem.pos && predefinedItem.rot) {
			const color = this.props.products[articleId as string]
				? this.props.products[articleId as string].filter.color
				: null;
			const object = this.logic.objectManager.createObject(
				articleId as string,
				{
					x: (predefinedItem.pos.x as number) / 10,
					y: (predefinedItem.pos.y as number) / 10,
					z: -((predefinedItem.pos.z as number) / 10),
				},
				false,
				color as string
			);

			// Convert rotation from deg to rad

			const ry = predefinedItem.rot.ry ? (Math.PI / 180) * predefinedItem.rot.ry : 0;

			if (object) {
				this.logic.objectManager.setRotation(object, { x: 0, y: ry, z: 0 });
				return object;
			}
		}
		return false;
	};

	private clearScene = (): void => {
		if (this.scenePointer) {
			this.scenePointer.clear();
		}
		this.logic.clearScene();
	};

	private getCoversToLoad(predefinedConfigurations: PredefinedConfiguration[]): string[] {
		const coverItemsToLoad: string[] = [];
		coverItemsToLoad.push(this.props.cover.coverName);
		for (let i = 0; i < predefinedConfigurations.length; i++) {
			if (!coverItemsToLoad.includes(predefinedConfigurations[i].defaultColor as string)) {
				coverItemsToLoad.push(predefinedConfigurations[i].defaultColor as string);
			}

			if (coverItemsToLoad.length === 8) {
				break;
			}
		}
		return coverItemsToLoad;
	}

	private getNotPreloadedCovers(coverItems: CoverItem[], coversLoaded: string[]): string[] {
		const coversNotPreloaded: string[] = [];
		for (let i = 0; i < coverItems.length; i++) {
			if (!coversLoaded.includes(coverItems[i].coverName)) {
				coversNotPreloaded.push(coverItems[i].coverName);
			}
		}

		return coversNotPreloaded;
	}

	private getLegUrl(): string {
		const legsModel = this.props.sofaModels[this.props.products['leg'].modelId];
		return AssetsUrls.createUrlForAssetId(
			this.props.assetFolder,
			this.props.rangeSettings.assets3dSrc,
			legsModel.modelId,
			this.props.rangeSettings.modelSwitchMap || null
		);
	}

	/**
	 * Render stuff.
	 *
	 * @returns Template.
	 */
	public render(): ReactNode {
		return (
			<Scene
				hide={this.props.loadingModels}
				deviceType={this.props.deviceType}
				platform={this.props.platform}
				onSceneReady={this.onSceneReady}
				onSceneResized={this.onSceneResized}
				onModelsPreloaded={this.onModelsPreloaded}
				onPointerOverNode={this.onPointerOverNode}
				onPointerOverGround={this.onPointerOverGround}
				onCameraUpdated={this.onCameraUpdated}
				showDebug={this.props.showDebug}
				sceneHeight={this.sceneHeight}
			/>
		);
	}
}

function mapStateToProps(state: RootState): StateProps {
	const { application, cover, models, checkout, swiper, actionButtons, scene } = state;
	const {
		removeAllSceneItems,
		environmentalLighting,
		lightIntensity,
		addCushions,
		sceneHelper,
		keepModule,
	} = scene;
	const { loadingModels, loadingCovers, assetFolder, showDebug, useIndividualAssets } = application;
	const { showFunctionSleep, showFunctionStorage, showFunctions, showMeasurements, showSelectAll } =
		actionButtons;
	const { sofaModels, sofaGroups } = models;
	const { moduleItems } = swiper;
	const { screenshotPending } = checkout;
	const { products } = state.products;
	const { unfinishedSection } = state.popOver;

	return {
		assetFolder,
		lightIntensity,
		environmentalLighting,
		orientation: state.deviceInfo.orientation,
		platform: state.deviceInfo.platform,
		deviceType: state.deviceInfo.deviceType,
		dragItemId: state.swiper.dragItemId as string,
		selectedItemId: state.swiper.selectedItemId as string,
		selectedConfiguration: state.sofaConfiguration.selectedConfiguration,
		settings: state.settings.settings,
		appView: state.application.appView,
		currentLightbox: state.application.currentLightbox,
		predefinedConfigurations: state.settings.predefinedConfigurations,
		updatePredefinedConfiguration: state.sofaConfiguration.updatePredefinedConfiguration,
		useMetricMeasures:
			state.localization &&
			state.localization.localization &&
			state.localization.localization.useMetricMeasures,
		rangeSettings: state.settings.rangeSettings as RangeSettings,
		coverItems: state.cover.coverItems,
		loadingModels,
		loadingCovers,
		resetCameraRotation: state.scene.resetCameraRotation,
		products,
		sofaModels,
		sofaGroups,
		moduleItems,
		cover: cover.selectedCover as CoverItem,
		showDebug,
		useIndividualAssets,
		showFunctionSleep,
		showFunctionStorage,
		showFunctions,
		showMeasurements,
		showSelectAll,
		selectedSceneItem: state.scene.selectedSceneItem as PopOverSceneItemData,
		screenshotPending,
		removeSceneItem: state.scene.removeSceneItem as SceneItemData,
		rotateSceneItem: state.scene.rotateSceneItem as SceneItemData,
		moveBackrest: state.scene.moveBackrest as SceneItemData,
		moveBackrestAsArmrest: state.scene.moveBackrestAsArmrest as SceneItemData,
		backrestAsArmrest: state.scene.backrestAsArmrest as SceneItemData,
		removeAllSceneItems,
		addCushions,
		setSceneItemCover: state.scene.setSceneItemCover as SceneItemCoverConnection,
		colorForAll: state.scene.colorForAll as string,
		animateCameraRotation: state.scene.animateCameraRotation as number,
		sceneHelper,
		writeDirection: state.localization.writeDirection,
		unfinishedSection,
		hasVisitScene: state.application.hasVisitScene,
		moduleItemsChanged: state.swiper.moduleItemsChanged,
		noValidConnectionPopoverShowing: state.popOver.noValidConnection.showPopOver,
		notPreloadedCovers: state.cover.notPreloadedCovers,
		selectedArmrest: state.armrest.selectedArmrest,
		armrests: state.armrest.armrests,
		coverOnly: cover.coverOnly as boolean,
		keepModule,
		isInUpptackaPlatform: state.application.isInUpptackaPlatform,
		swiperEventType: state.swiper.eventType,
		specificItems: state.scene.specificSelectedItems as Map<string, SceneObject>,
		moduleSwiperItems: state.swiper.moduleItems,
	};
}

function mapDispatchToProps(dispatch: AppDispatch): DispatchProps {
	return {
		setDragItemId: (value: string | null): void => {
			dispatch(setDragItemId(value, 'scene'));
			dispatch(setShowFunctionStorage(false, false));
			dispatch(setShowFunctionSleep(false, false));
			dispatch(clearAdditionalCoverItems());
		},
		setSwiperEventType: (value: {
			type: 'click' | 'drag' | null;
			productId: string | null;
		}): void => {
			dispatch(setSwiperEventType(value));
		},
		setSelectedItemId: (value: string | null): void => {
			dispatch(setSelectedItemId(value));
		},
		setNoRotationIcon: (value: boolean): void => {
			dispatch(setNoRotationIcon(value));
		},
		setHasFirstInteractionHappened: (value: boolean): void => {
			dispatch(setHasFirstInteractionHappened(value));
		},
		dragSceneItemId: (value: string | null): void => {
			dispatch(dragSceneItemId(value));
		},
		overSceneItemId: (value: string | null): void => {
			dispatch(overSceneItemId(value));
		},
		pointerOverDropArea: (value: boolean): void => {
			dispatch(pointerOverDropArea(value));
		},
		removeSceneItemOutsideScene: (sceneObject: SceneObject): void => {
			dispatch(removeSceneItemOutsideScene(sceneObject));
			dispatch(clearAdditionalCoverItems());
		},
		saveSceneData: (
			sceneItems: SceneItemData[],
			sofaConfiguration: SofaConfiguration,
			articlesPlaceable: { [key: string]: boolean },
			backrestsMissingCushion: boolean,
			rangeSettings: RangeSettings,
			sofaModels: ModelData,
			products: ProductData,
			isOverDropArea: boolean
		): void => {
			dispatch(
				setSceneData(
					sceneItems,
					sofaConfiguration,
					articlesPlaceable,
					backrestsMissingCushion,
					rangeSettings,
					sofaModels,
					products,
					isOverDropArea
				)
			);
		},
		setShoppingItems: (shoppingItems: ShoppingItem[]): void => {
			dispatch(setShoppingItems(shoppingItems));
		},
		setLoadModels: (value: boolean): void => {
			dispatch(setLoadingModels(value));
		},
		setLoadCovers: (value: boolean): void => {
			dispatch(setLoadingCovers(value));
		},
		setResetCameraRotation: (value: boolean): void => {
			dispatch(resetCameraRotation(value));
		},
		showPopOver: (popOver: PopOver): void => {
			dispatch(showPopOver(popOver));
		},
		hidePopOver: (popOver: PopOver): void => {
			dispatch(hidePopOver(popOver));
		},
		setScreenshotPending: (): void => {
			dispatch(screenshotPending());
		},
		saveScreenshot: (screenshot: string): void => {
			dispatch(screenshotSuccess(screenshot));
		},
		selectSceneItem: (item: PopOverSceneItemData | null): void => {
			dispatch(selectSceneItem(item));
			if (item) {
				dispatch(selectSceneArticle(item.articleId));
			}
		},
		setRotateSceneItem: (item: SceneItemData | null): void => {
			dispatch(rotateSceneItem(item));
		},
		setMoveBackrest: (item: SceneItemData | null): void => {
			dispatch(moveBackrest(item));
		},
		setMoveBackrestAsArmrest: (item: SceneItemData | null): void => {
			dispatch(moveBackrestAsArmrest(item));
		},
		setBackrestAsArmrest: (item: SceneItemData | null): void => {
			dispatch(backrestAsArmrest(item));
		},
		setRemoveAllSceneItems: (value: boolean): void => {
			dispatch(removeAllSceneItems(value));
		},
		setAddCushions: (value: boolean): void => {
			dispatch(addCushions(value));
		},
		changeSceneItemCover: (sceneItemCoverConnection: SceneItemCoverConnection | null): void => {
			dispatch(setSceneItemCover(sceneItemCoverConnection));
		},
		setColorForAll: (value: string | null): void => {
			dispatch(setColorForAll(value));
		},
		setShowSelectAll: (value): void => {
			dispatch(setShowSelectAll(value));
		},
		setCoverItem: (cover: CoverItem, coverUpdated: boolean): void => {
			dispatch(setCoverItem(cover, coverUpdated));
		},
		setShowFunctionSleep: (value, track): void => {
			dispatch(setShowFunctionSleep(value, track));
		},
		setShowFunctionStorage: (value, track): void => {
			dispatch(setShowFunctionStorage(value, track));
		},
		setShowFunctions: (value): void => {
			dispatch(setShowFunctions(value));
		},
		setShowMeasurements: (value, track): void => {
			dispatch(setShowMeasurements(value, track));
		},
		setMeasurements: (value): void => {
			dispatch(setMeasurements(value));
		},
		setAnimateCameraRotation: (value: number | null): void => {
			dispatch(animateCameraRotation(value));
		},
		setCurrentLightbox: (lightbox: Lightbox): void => {
			dispatch(setCurrentLightbox(lightbox));
		},
		setUpdatePredefinedConfiguration: (value: boolean): void => {
			dispatch(setUpdatePredefinedConfiguration(value));
		},
		setPersistedConfiguration: (value: string): void => {
			dispatch(setPersistedConfiguration(value));
		},
		setFaultySections: (
			unsfinishedSections: PopOverSceneItemData[],
			missingCoverSections: PopOverSceneItemData[],
			standaloneSofabed: PopOverSceneItemData[]
		): void => {
			dispatch(setFaultySections(unsfinishedSections, missingCoverSections, standaloneSofabed));
		},
		setSceneHelper: (value: SceneHelperData): void => {
			dispatch(setSceneHelper(value));
		},
		setUnfinishedModule: (value: PopOverSceneItemData): void => {
			dispatch(setUnfinishedModule(value));
		},
		setModuleItemsChanged: (value: boolean): void => {
			dispatch(setModuleItemsChanged(value));
		},
		setCoverChanged: (value: Lightbox | null): void => {
			dispatch(setCoverChanged(value));
		},
		setNotPreloadedCovers: (value: string[] | null): void => {
			dispatch(setNotPreloadedCovers(value));
		},
		setSelectedArmrest: (value: Product | null, track: boolean): void => {
			dispatch(setSelectedArmrest(value, track));
		},
		setKeepModule: (value: boolean): void => {
			dispatch(setKeepModule(value));
		},
		rotateCamera: (): void => {
			dispatch(setRotateCamera());
		},
		setDisableMoveBackrest: (value: boolean): void => {
			dispatch(setDisableMoveBackrest(value));
		},
		setDisableMoveArmrestAsBackrest: (value: boolean): void => {
			dispatch(setDisableMoveBackrestAsArmrest(value));
		},
		clearAdditionalCoverItems: (): void => {
			dispatch(clearAdditionalCoverItems());
		},
		setTwoSeparateSofas: (value: boolean): void => {
			dispatch(setTwoSeparateSofas(value));
		},
		setSpecificItems: (items: Map<string, SceneObject>): void => {
			dispatch(setSpecificSelectedItems(items));
		},
		setBuildTipHasBeenShown: (value: boolean): void => {
			dispatch(buildTipHasBeenShown(value));
		},
		setHideBuildTip: (value: boolean): void => {
			dispatch(hideBuildTip(value));
		},
	};
}

export default connect<StateProps, DispatchProps, OwnProps, RootState>(
	mapStateToProps,
	mapDispatchToProps,
	null,
	{ forwardRef: true }
)(SceneContainer as ComponentType);
